import { useLayoutEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import ListButton from "@/components/list-button"
import { faXmark } from "@fortawesome/free-solid-svg-icons"
import SidePanel from "@/components/SidePanel"
import { EstablishmentScanResult } from "../model/establishment.types"
import EstablishmentScanResultCard, { ScanResultKind } from "./EstablishmentScanResultCard"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import establishmentStore from "../model/establishment.store"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import PhysicalRiskEditModal from "@/entities/physical-risk/ui/modal/PhysicalRiskEditModal"

//Doublons purs d'abord : ils se fusionnent sans perte. Puis ce qui reste a nettoyer, toujours affiche.
const CATEGORIES: {
  title: string
  description: string
  kind: ScanResultKind
  filter: (item: EstablishmentScanResult) => boolean
}[] = [
  {
    title: "Doublons",
    description: "Même risque, même évaluation. Ils peuvent être regroupés.",
    kind: "merge",
    filter: (item) => item.tag === "Duplicate" && Boolean(item.isSameEvaluation)
  },
  {
    title: "Évaluations différentes",
    description: "Même risque, évalué différemment selon les unités.",
    kind: "merge",
    filter: (item) => item.tag === "Duplicate" && !item.isSameEvaluation
  },
  {
    title: "Même intitulé",
    description: "Même intitulé, mais un contenu différent. À vérifier avant de regrouper.",
    kind: "merge",
    filter: (item) => item.tag === "SameName"
  },
  {
    title: "Risques sans famille",
    description: "À rattacher à une famille de risques.",
    kind: "open",
    filter: (item) => item.tag === "NoFamily"
  },
  {
    title: "À passer en RPS",
    description:
      "Risques psychosociaux saisis comme des risques physiques. Chaque fonction du risque reçoit une situation dans la matrice RPS, le risque est supprimé.",
    kind: "rps",
    filter: (item) => item.tag === "ToRps"
  }
]

interface Props {
  items: EstablishmentScanResult[]
  onClose: () => void
  onWidthChange: (width: number) => void
}

export default function EstablishmentScanPanel({ items, onClose, onWidthChange }: Props) {
  const dispatch = useAppDispatch()
  const risks = useAppSelector((state) => state.physicalRisk.list)
  const [editingRiskId, setEditingRiskId] = useState<string | null>(null)
  const editingRisk = risks.find((risk) => risk.id === editingRiskId)
  const [busyId, setBusyId] = useState<string | null>(null)
  //Action refusee par le serveur (groupe perime, risque sans fonction...) : son message
  const showError = (action: { payload?: any }) => action.payload?.error && toast.error(action.payload.error)
  const visibleItems = items.filter((item) => CATEGORIES.some((category) => category.filter(item)))
  const panelRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const panel = panelRef.current
    if (!panel) return
    //La largeur reelle suit aussi le repli et la transition du SidePanel.
    const updateWidth = () => onWidthChange(panel.getBoundingClientRect().width)
    updateWidth()
    const observer = new ResizeObserver(updateWidth)
    observer.observe(panel)
    return () => {
      observer.disconnect()
      onWidthChange(0)
    }
  }, [onWidthChange])

  return (
    <>
      {editingRisk && <PhysicalRiskEditModal risk={editingRisk} onClose={() => setEditingRiskId(null)} />}
      <div ref={panelRef} className="fixed bottom-0 right-0 top-[48px] z-40 bg-white shadow-custom">
        <SidePanel side="right" width={380}>
          <div className="flex flex-col gap-[20px] p-[24px]">
            <div className="flex items-center gap-[10px]">
              <h2 className="m-0 min-w-0 flex-1 text-lg">Revue</h2>
              <ListButton icon={faXmark} text="Fermer" onClick={onClose} />
            </div>
            <div className="grey-t text-base">{visibleItems.length} suggestions</div>
            {CATEGORIES.map((category) => {
              const results = visibleItems.filter(category.filter)
              return (
                <EstablishmentScanResultCard
                  key={category.title}
                  title={category.title}
                  description={category.description}
                  kind={category.kind}
                  results={results}
                  busyId={busyId}
                  onOpenRisk={async (id) => {
                    if (risks.some((risk) => risk.id === id)) {
                      setEditingRiskId(id)
                      return
                    }
                    const response = await dispatch(physicalRiskStore.thunks.fetchAndSet())
                    if (Array.isArray(response.payload) && response.payload.some((risk) => risk.id === id)) {
                      setEditingRiskId(id)
                    }
                  }}
                  onMoveToRps={async (id, riskId, axis) => {
                    setBusyId(id)
                    try {
                      showError(await dispatch(establishmentStore.thunks.moveRiskToRps({ riskId, axis })))
                    } finally {
                      setBusyId(null)
                    }
                  }}
                  onMerge={async (id) => {
                    setBusyId(id)
                    try {
                      showError(await dispatch(establishmentStore.thunks.mergeRisks(id)))
                    } finally {
                      setBusyId(null)
                    }
                  }}
                />
              )
            })}
          </div>
        </SidePanel>
      </div>
    </>
  )
}
